'use client'

import { VARS } from '@/animations'
import { useGSAPMedia } from '@/hooks'
import { useStoreCursor } from '@/store'
import { MEDIA } from '@config'
import { ScrollTrigger, gsap } from '@gsap'
import clsx from 'clsx'
import { forwardRef, memo, useImperativeHandle, useRef } from 'react'

const GradientBgRef = forwardRef((props, ref) => {
  /* ---------------------------------- refs ---------------------------------- */

  const el = useRef<any>()
  const containerRef = useRef<any>()

  /* --------------------------- //API - move cursor -------------------------- */

  useImperativeHandle(ref, () => {
    // return our API
    const xTo = gsap.quickTo(el.current, 'x', {
      duration: 0.66,
      ease: VARS.ease.out,
      delay: 0.1
    })
    const yTo = gsap.quickTo(el.current, 'y', {
      duration: 0.66,
      ease: VARS.ease.out,
      delay: 0.1
    })

    return {
      moveTo(x: number, y: number) {
        xTo(x)
        yTo(y)
      },
      container: containerRef.current // <-- aggiungi questa riga per esporre il DOM node
    }
  }, [])

  const elemsCommonStyles =
    'md:blur-[300px] md:rounded-round pointer-events-none absolute w-[150%] md:w-[80%] -translate-x-[50%] -translate-y-[50%]'

  return (
    <>
      <div
        className={clsx(
          elemsCommonStyles,
          'md:bg-yellow z-10 aspect-[10/8] opacity-50 will-change-transform max-md:bg-[radial-gradient(closest-side,var(--color-yellow),transparent)]'
        )}
        ref={el}
        {...props}
      ></div>

      <div
        className={clsx(
          elemsCommonStyles,
          'md:bg-azure -top-[10%] left-[90%] aspect-[2/1] will-change-transform max-md:bg-[radial-gradient(closest-side,var(--color-azure),transparent)]'
        )}
      ></div>
    </>
  )
})

GradientBgRef.displayName = 'GradientBg'

interface GradientBgProps extends React.HTMLAttributes<HTMLDivElement> {}

const GradientBg = ({ ...props }: GradientBgProps) => {
  const ref = useRef<any>()
  const containerRef = useRef<HTMLDivElement>(null)

  const hasMoved = useStoreCursor.use.hasMoved()
  const setHasMoved = useStoreCursor.use.setHasMoved()

  const moveCursor = (x: number, y: number) => {
    if (!ref.current) return
    hasMoved === false && setHasMoved(true)
    ref.current.moveTo(x, y)
  }

  useGSAPMedia({
    media: MEDIA.md,
    scope: containerRef,
    callback: () => {
      gsap.registerPlugin(ScrollTrigger)

      ScrollTrigger.observe({
        target: window,
        type: 'pointer',
        onMove: (self) => {
          let offsetY = containerRef.current?.getBoundingClientRect().y || 0
          moveCursor(self.x as number, (self.y || 0 + offsetY) as number)
        }
      })
    }
  })

  return (
    <div
      ref={containerRef}
      className="gradientBg bg-primary-300 absolute inset-0 -z-1"
    >
      <GradientBgRef ref={ref} {...props} />
    </div>
  )
}
export default memo(GradientBg)
